import { Text } from "@hackthenorth/north";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { User } from "src/api/types.generated";
import { GOOSE_GAME_POINTS_NAME } from "src/shared/constants/gooseGames";
import { useUserContext } from "src/shared/contexts";
import { useDeviceSize } from "src/shared/hooks";
import { EmailIcon } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { resolvePal } from "src/views/goose-games/hackPals";
import { GGPlayer } from "src/views/goose-games/types";

import { BalanceChip } from "../../balanceChip";
import HackPalBanner from "../../hackPalBanner";
import {
  MEDAL_PROGRESS_MAX,
  medalProgression,
} from "../../modals/hacker/home/quest/constants";
import AddPointsModal from "../../modals/organizer/hacker_scan/add_points";
import ProgressionToolTip from "../../ProgressionToolTip";

interface Props {
  profile?: Pick<User, "name" | "email">;
  palNumber?: number;
  ggPlayer?: GGPlayer;
  refetchHacker?: () => void;
}

const ProfileCard = ({
  profile,
  palNumber,
  ggPlayer,
  refetchHacker,
}: Props) => {
  const { isOrganizer } = useUserContext();

  const [pointsModalOpen, setPointsModalOpen] = useState(false);

  const isMediumOrSmaller = useDeviceSize("medium");

  const totalBalance = ggPlayer?.total_points ?? 0;
  const currentBalance = ggPlayer?.current_points ?? 0;

  const onConfirm = () => {
    setPointsModalOpen(false);
    if (refetchHacker) refetchHacker();
  };

  const onCancel = () => {
    setPointsModalOpen(false);
  };

  return (
    <>
      <Card>
        <RowWrapper>
          <PositionWrapper>
            <HackPalBanner
              balance={totalBalance}
              hackpal={resolvePal(palNumber).image}
            />
          </PositionWrapper>
          <ContentWrapper>
            <FlexHeader>
              <DetailsWrapper>
                <StyledTitle
                  mods="heading title"
                  length={profile ? profile.name.length : 0}
                >
                  {profile ? profile.name : ""}
                </StyledTitle>
                <EmailContainer>
                  <StyledEmailIcon />
                  <StyledSubText>{profile ? profile.email : ""}</StyledSubText>
                </EmailContainer>
              </DetailsWrapper>
              <ButtonWrapper>
                <BalanceDetailsWrapper>
                  <BalanceWrapper>
                    <StyledSubText upper mods="caption">
                      Current Balance
                    </StyledSubText>
                    <BalanceChip
                      points={ggPlayer ? ggPlayer.current_points : 0}
                    />
                  </BalanceWrapper>
                  <BalanceWrapper>
                    <StyledSubText upper mods="caption">
                      All-Time Balance
                    </StyledSubText>
                    <BalanceChip
                      points={ggPlayer ? ggPlayer.total_points : 0}
                      isAllTime
                    />
                  </BalanceWrapper>
                </BalanceDetailsWrapper>
                {isOrganizer && ggPlayer && (
                  <Button
                    leadingIcon={<PlusCircleIcon width={16} color="white" />}
                    size="md"
                    color="primary"
                    onClick={() => {
                      setPointsModalOpen(true);
                    }}
                  >
                    Add {GOOSE_GAME_POINTS_NAME}
                  </Button>
                )}
              </ButtonWrapper>
            </FlexHeader>
            {!isMediumOrSmaller && <ProgressBar totalBalance={totalBalance} />}
          </ContentWrapper>
        </RowWrapper>
        {isMediumOrSmaller && <ProgressBar totalBalance={totalBalance} />}
      </Card>

      {ggPlayer && (
        <AddPointsModal
          isOpen={pointsModalOpen}
          onConfirm={onConfirm}
          onCancel={onCancel}
          currentBalance={currentBalance}
          eventAttendeeId={ggPlayer.event_attendee_id}
        />
      )}
    </>
  );
};

const ProgressBar = ({ totalBalance }: { totalBalance: number }) => {
  const progressBackgroundRef = useRef<HTMLDivElement>(null);
  const progressForegroundRef = useRef<HTMLDivElement>(null);

  const markers = Object.keys(medalProgression);

  const currMilestone =
    markers
      .map(Number)
      .sort((a, b) => b - a)
      .find((milestone) => totalBalance >= milestone) ?? null;

  const visibleMarkers = markers.filter(
    (amount) => Number(amount) <= MEDAL_PROGRESS_MAX
  );

  return (
    <ProgressWrapper>
      <ProgressBackground ref={progressBackgroundRef}>
        <ProgressHoverTarget data-tip data-for={"progression-tooltip"} />
        <ProgressionToolTip
          currMilestone={currMilestone}
          progressBackgroundRef={progressBackgroundRef}
          progressForegroundRef={progressForegroundRef}
        />
        {visibleMarkers.map((amount) => (
          <Marker
            key={`Marker ${amount}`}
            progress={Number(amount)}
            isTooltip={false}
            align={undefined}
          />
        ))}
        {totalBalance > 0 && (
          <ProgressForeground
            ref={progressForegroundRef}
            progress={totalBalance}
          />
        )}
      </ProgressBackground>
      <MarkerBackground>
        {visibleMarkers.map((amount) => (
          <MarkerText key={amount} progress={Number(amount)}>
            {medalProgression[Number(amount)].emoji}
            {amount}
          </MarkerText>
        ))}
      </MarkerBackground>
    </ProgressWrapper>
  );
};

const Card = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: start;
  justify-content: end;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid var(--North-Border-Gray, #d1d5db);
  background: ${({ theme }) => theme.globalConstants.color.white};
  margin-bottom: 24px;

  ${mediaQueries.largeMobile} {
    border: none;
    border-radius: none;
    background: none;
  }
`;

const RowWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: start;
  justify-content: end;
  gap: 4px;
`;

const ProgressBackground = styled.div`
  position: relative;
  height: 17px;
  width: 100%;
  border-radius: 24px;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundGray};

  ${mediaQueries.tablet} {
    height: 10px;
  }
`;

const ProgressHoverTarget = styled.div`
  position: absolute;
  inset: 0;
  z-index: 6;
  cursor: pointer;
  border-radius: 24px;
`;

const ProgressForeground = styled.div<{ progress: number }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  height: 17px;
  width: ${({ progress }) =>
    `min(calc(${(progress / MEDAL_PROGRESS_MAX) * 100}% + 5px), 100%)`};
  border-radius: 24px;
  background-color: ${({ theme }) => theme.globalConstants.color.bluePrimary1};

  ${mediaQueries.tablet} {
    height: 10px;
  }
`;

const StyledTitle = styled(Text)<{ length: number }>`
  font-size: ${({ length }) =>
    length > 10 ? "32px !important" : "36px !important"};
  line-height: normal !important;
  margin-bottom: 0px !important;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const MarkerBackground = styled.div`
  position: relative;
  height: 28px;
  width: 100%;

  ${mediaQueries.medium} {
    margin-bottom: 8px;
  }

  ${mediaQueries.tablet} {
    margin-bottom: 8px;
  }
`;

const MarkerText = styled(Text)<{ progress: number }>`
  position: absolute;
  left: ${({ progress }) => {
    const percent = (progress / MEDAL_PROGRESS_MAX) * 100;
    return `calc(min(max(${percent}% + 5.5px, 10px), 100% - 15px))`;
  }};
  display: flex !important;
  width: 0;
  line-height: normal !important;
  justify-content: center;
  font-size: 16px !important;
  white-space: nowrap;
  font-weight: 500;

  ${mediaQueries.tablet} {
    left: ${({ progress }) => {
      const percent = (progress / MEDAL_PROGRESS_MAX) * 100;
      return `calc(min(max(${percent}% + 2.5px, 12px), 100% - 18px))`;
    }};
    font-size: 16px !important;
  }
`;

const Marker = styled.div<{
  progress: number;
  isTooltip: boolean;
  align: "left" | "center" | "right" | undefined;
}>`
  position: absolute;
  display: flex;
  justify-content: ${({ align }) =>
    align === "left" ? "start" : align === "right" ? "end" : "center"};
  left: ${({ progress, align }) => {
    const percent = (progress / MEDAL_PROGRESS_MAX) * 100;
    const alignmentPadding =
      align === "left" ? -12 : align === "right" ? 12 : 0;
    return `calc(min(max(${percent}%, 5px), 100% - 13px) + ${alignmentPadding}px)`;
  }};
  z-index: 5;
  width: 10px;
  height: ${({ isTooltip }) => (isTooltip ? "auto" : "10px")};
  background-color: ${({ theme, isTooltip }) =>
    isTooltip ? "transparent" : theme.globalConstants.color.white};
  border-radius: 32px;
  top: 50%;
  transform: translate(0, -50%);

  ${mediaQueries.tablet} {
    left: ${({ progress, align }) => {
      const percent = (progress / MEDAL_PROGRESS_MAX) * 100;
      const alignmentPadding =
        align === "left" ? -6 : align === "right" ? 6 : 0;
      return `calc(min(max(${percent}%, 3.5px), 100% - 8.5px) + ${alignmentPadding}px)`;
    }};
    width: 7px;
    height: ${({ isTooltip }) => (isTooltip ? "auto" : "7px")};
  }

  &:hover {
    z-index: 10;
  }
`;

const PositionWrapper = styled.div`
  position: absolute;
  top: -10px;
  left: 0px;
  width: 225px;
  padding-bottom: 16px;

  ${mediaQueries.medium} {
    width: 200px;
  }

  ${mediaQueries.tablet} {
    width: 150px;
  }

  ${mediaQueries.largeMobile} {
    top: 0;
  }
`;

const ContentWrapper = styled.div`
  padding-block: 32px;
  padding-inline: 50px;
  display: flex;
  flex-direction: column;
  width: calc(100% - 225px);
  float: inline-end;
  gap: 24px;

  ${mediaQueries.medium} {
    width: calc(100% - 200px);
  }

  ${mediaQueries.tablet} {
    padding-bottom: 24px;
    width: calc(100% - 150px);
    padding-inline: 16px;
  }

  ${mediaQueries.largeMobile} {
    padding-top: 0px;
    padding-bottom: 24px;
    width: calc(100% - 150px);
    padding-inline: 12px;
  }
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  float: inline-end;
  gap: 16px;

  ${mediaQueries.medium} {
    padding-inline: 50px;
  }

  ${mediaQueries.tablet} {
    padding-inline: 16px;
    gap: 8px;
  }

  ${mediaQueries.largeMobile} {
    padding-inline: 12px;
    gap: 8px;
  }
`;

const FlexHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 8px;

  ${mediaQueries.medium} {
    flex-direction: column;
    gap: 24px;
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
  overflow: hidden;
  width: 100%;
`;

const BalanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 4px;

  ${mediaQueries.medium} {
    align-items: start;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const BalanceDetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;

  ${mediaQueries.largeMobile} {
    gap: 12px;
  }
`;

const EmailContainer = styled.div`
  display: flex;
  gap: 4px;
  white-space: nowrap;
  text-overflow: ellipsis;
  align-items: center;
`;

const StyledEmailIcon = styled(EmailIcon)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  height: 16px;
`;

const StyledSubText = styled(Text)<{ upper?: boolean }>`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
  font-size: ${({ upper }) => (upper ? "12px" : "16px")} !important;
  font-weight: 500;
  line-height: normal !important;
  text-transform: ${({ upper }) => (upper ? "uppercase" : undefined)};
  text-align: end;
  overflow-x: hidden;
  text-overflow: ellipsis;

  ${mediaQueries.medium} {
    text-align: start;
  }

  ${mediaQueries.largeMobile} {
    font-size: ${({ upper }) => (upper ? "10px" : "14px")} !important;
  }
`;

export default ProfileCard;
